import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CircularProgress, Box, Alert, Container, Button } from '@mui/material';
import { checkUserAccess, AccessCheckResult } from '../../utils/accessControl';
import { Link as RouterLink } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading, signOut } = useAuth();
  const [accessCheck, setAccessCheck] = useState<AccessCheckResult | null>(null);
  const [checking, setChecking] = useState(true);

  // 🚀 Bypass para testes E2E e desenvolvimento
  const urlParams = new URLSearchParams(window.location.search);
  const testMode = urlParams.get('test') === '1';
  
  const BYPASS = import.meta.env.VITE_BYPASS_AUTH === 'true' || 
                 import.meta.env.MODE === 'test' ||
                 testMode;
  
  useEffect(() => {
    async function verifyAccess() {
      if (!user) {
        setChecking(false);
        return;
      }

      const result = await checkUserAccess();
      setAccessCheck(result);
      setChecking(false);
    }

    verifyAccess();
  }, [user]);

  // Early return para bypass (depois dos hooks)
  if (BYPASS) {
    if (testMode) console.warn('⚠️ TEST MODE ATIVO - Query string ?test=1 detectada!');
    else console.warn('⚠️ AUTH BYPASS ATIVO - Apenas para desenvolvimento/testes!');
    return <>{children}</>;
  }

  // Loading de autenticação
  if (authLoading || checking) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Não autenticado
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Acesso negado (pendente ou rejeitado)
  if (accessCheck && !accessCheck.hasAccess) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Box>
            <Alert 
              severity={accessCheck.reason === 'rejected' ? 'error' : 'warning'}
              sx={{ mb: 2 }}
            >
              <strong>
                {accessCheck.reason === 'pending' && 'Acesso Pendente'}
                {accessCheck.reason === 'rejected' && 'Acesso Negado'}
                {accessCheck.reason === 'not_approved' && 'Acesso Não Aprovado'}
              </strong>
              <br />
              {accessCheck.message}
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => signOut()}
              >
                Fazer Logout
              </Button>
              
              {accessCheck.reason === 'rejected' && (
                <Button
                  component={RouterLink}
                  to="/solicitar-acesso"
                  variant="contained"
                >
                  Solicitar Novamente
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    );
  }

  // Acesso aprovado
  return <>{children}</>;
}
